import { format } from "date-fns";
import { allocateByPercentage } from "../../../../helper/allocate_by_percentage";
import { formatToPHP } from "../../../../utils/numberFormatter";
import { toCents, getBalancePHP } from "./SplitAllocationModal";
import { isSourceComplete, toAccountOption } from "./SourceAccountFields";

// ---------------------------------------------------------------------------
// Mappers between saved rows, page split lines and save payloads
// ---------------------------------------------------------------------------

// Saved allocation row -> split line used by the page and the modal
export const toSplitLine = (row) => ({
  paymentMethod: row.payment_method,
  subject: row.source_subject
    ? { value: row.source_subject.id, label: row.source_subject.subject_name }
    : null,
  account: row.source_account ? toAccountOption(row.source_account) : null,
  chequeNumber: row.cheque_number || "",
  maturityDate: row.maturity_date || "",
  remarks: row.remarks || "",
  amount: String(row.amount),
});

// Split line -> payload line for the save endpoint
export const toPayloadLine = (line) => ({
  payment_method: line.paymentMethod,
  source_subject_id: line.subject?.value,
  source_account_id: line.account?.value,
  amount: toCents(line.amount) / 100,
  cheque_number: line.paymentMethod === "Bank" ? line.chequeNumber : null,
  maturity_date:
    line.paymentMethod === "Bank" && line.maturityDate
      ? format(new Date(line.maturityDate), "yyyy-MM-dd")
      : null,
  remarks: line.remarks,
});

// ---------------------------------------------------------------------------
// Line helpers
// ---------------------------------------------------------------------------

const isCashLine = (line) => line.paymentMethod === "Cash";

// Bank line paid by cheque: issued as a Pending check on approval
const isChequeLine = (line) =>
  line.paymentMethod === "Bank" && Boolean(line.chequeNumber?.trim());

// Bank line without a cheque: a Pending bank transaction on approval, or a
// Confirmed one (deducted right away) when a Management user approves
const isBankTransferLine = (line) =>
  line.paymentMethod === "Bank" && !line.chequeNumber?.trim();

const sumLinesCents = (lines) =>
  lines.reduce((sum, line) => sum + toCents(line.amount), 0);

const joinOwnerNames = (owners) =>
  owners.map((owner) => owner.account_name).join(", ");

// ---------------------------------------------------------------------------
// Summary: everything the page derives from owners, splits and the default
// source. The server checks the same rules again on approval.
// ---------------------------------------------------------------------------

export const buildFundAllocationSummary = ({
  netIncome,
  owners,
  splits,
  defaultSource,
  isAutoConfirmBank = false, // Management approver (see isBankTransferLine)
}) => {
  // Nothing is distributed from a net loss
  const amountToDistribute = netIncome > 0 ? netIncome : 0;

  // Each owner's share of the net income (PHP), based on their investment
  const allocations = allocateByPercentage(
    amountToDistribute,
    owners.map((owner) => ({
      id: owner.id,
      percentage: owner.shared_percentage,
    })),
  );
  const getAllocation = (owner) => allocations[owner.id] || 0;

  // Where an owner's allocation comes from: their custom split, or the whole
  // amount from the default source account
  const getOwnerLines = (owner) => {
    if (splits[owner.id]) return splits[owner.id];
    if (!defaultSource.account) return [];
    return [{ ...defaultSource, amount: String(getAllocation(owner)) }];
  };

  const allLines = owners.flatMap(getOwnerLines);

  // Part of an owner's allocation not covered by their saved split yet
  // (a split can be saved partially and finished later)
  const getUnallocatedCents = (owner) =>
    toCents(getAllocation(owner)) - sumLinesCents(getOwnerLines(owner));

  const hasAllocation = (owner) => getAllocation(owner) > 0;

  return {
    amountToDistribute,
    allocations,
    getAllocation,
    getOwnerLines,
    getUnallocatedCents,

    // Owners whose allocation has no source account yet
    unfundedOwners: owners.filter(
      (owner) => hasAllocation(owner) && getOwnerLines(owner).length === 0,
    ),

    partiallySplitOwners: owners.filter(
      (owner) => splits[owner.id] && getUnallocatedCents(owner) > 0,
    ),

    // Owners still on the default source; saved with it right before approving
    ownersOnDefault: owners.filter(
      (owner) => !splits[owner.id] && hasAllocation(owner),
    ),

    // A Bank default source still needs its maturity date
    isDefaultIncomplete:
      Boolean(defaultSource.account) && !isSourceComplete(defaultSource),

    overdrawnAccounts: getOverdrawnAccounts(allLines, isAutoConfirmBank),
    isAutoConfirmBank,

    // Cash paid out on approval; Bank cheques created as Pending; bank
    // transactions created as Pending, or Confirmed for Management (cents)
    cashTotalCents: sumLinesCents(allLines.filter(isCashLine)),
    checkTotalCents: sumLinesCents(allLines.filter(isChequeLine)),
    bankTransferTotalCents: sumLinesCents(allLines.filter(isBankTransferLine)),
  };
};

// Source accounts whose balance can't cover what approval takes from them:
// Cash, plus Bank without a cheque when a Management user approves (it is
// confirmed right away). Bank cheques are deducted when the issued check is
// confirmed, and an Employee's bank transactions when they are confirmed, so
// those don't count against the balance here.
// Returns [{ account, cents }] (cents in PHP).
const getOverdrawnAccounts = (lines, isAutoConfirmBank) => {
  const usageByAccount = {};
  const isDeductedOnApproval = (line) =>
    isCashLine(line) || (isAutoConfirmBank && isBankTransferLine(line));

  lines.filter(isDeductedOnApproval).forEach((line) => {
    const usage = (usageByAccount[line.account.value] ||= {
      account: line.account.account,
      cents: 0,
    });
    usage.cents += toCents(line.amount);
  });

  return Object.values(usageByAccount).filter(
    (usage) => usage.cents > toCents(getBalancePHP(usage.account)),
  );
};

// ---------------------------------------------------------------------------
// Messages
// ---------------------------------------------------------------------------

export const describeOverdrawnAccount = (usage) =>
  `"${usage.account.account_name}" only has ${formatToPHP(
    getBalancePHP(usage.account),
  )}, but ${formatToPHP(usage.cents / 100)} is allocated from it`;

// Approve is disabled with this reason while a source account can't cover
// what approval deducts from it; Reject moves no money, so it stays available
export const getInsufficientBalanceReason = ({ overdrawnAccounts }) =>
  overdrawnAccounts.length > 0
    ? `Insufficient balance: ${overdrawnAccounts
        .map(describeOverdrawnAccount)
        .join("; ")}.`
    : null;

// Each rule that blocks approval: when it fails, its message is shown
const APPROVAL_RULES = [
  {
    fails: (s) => s.amountToDistribute <= 0,
    message: () => "There is no net income to distribute.",
  },
  {
    fails: (s) => s.unfundedOwners.length > 0,
    message: (s) =>
      `No source account yet for: ${joinOwnerNames(s.unfundedOwners)}.`,
  },
  {
    fails: (s) => s.partiallySplitOwners.length > 0,
    message: (s) =>
      `Still to allocate for: ${joinOwnerNames(s.partiallySplitOwners)}.`,
  },
  {
    fails: (s) => s.ownersOnDefault.length > 0 && s.isDefaultIncomplete,
    message: () => "Enter the maturity date of the default Bank source.",
  },
  {
    fails: (s) => s.overdrawnAccounts.length > 0,
    message: () => "Some source accounts don't have enough balance.",
  },
];

// Messages of every rule that still blocks approval
export const getApprovalIssues = (summary) =>
  APPROVAL_RULES.filter((rule) => rule.fails(summary)).map((rule) =>
    rule.message(summary),
  );

// Text of the approve confirmation dialog
export const getApproveConfirmText = ({
  cashTotalCents,
  checkTotalCents,
  bankTransferTotalCents,
  ownersOnDefault,
  isAutoConfirmBank,
}) => {
  const paragraphs = [
    `Cash payments totaling ${formatToPHP(
      cashTotalCents / 100,
    )} will be deducted from their source accounts.`,
  ];

  if (checkTotalCents > 0) {
    paragraphs.push(
      `Bank cheques totaling ${formatToPHP(
        checkTotalCents / 100,
      )} will be issued as Pending checks.`,
    );
  }

  if (bankTransferTotalCents > 0) {
    paragraphs.push(
      `Bank payments without a cheque totaling ${formatToPHP(
        bankTransferTotalCents / 100,
      )} will be ${
        isAutoConfirmBank
          ? "confirmed in Bank Transaction and deducted from their source accounts."
          : "created as Pending bank transactions."
      }`,
    );
  }

  if (ownersOnDefault.length > 0) {
    paragraphs.push(
      `The default source account will first be saved for: ${joinOwnerNames(
        ownersOnDefault,
      )}.`,
    );
  }

  paragraphs.push("This can't be undone.");
  return paragraphs.join("\n\n");
};
